import React, { useEffect } from "react";

import {
  FarmFootprintingCapabilities,
  TFunction,
  formatFractionInput,
  getFarmFootprintingCapabilities,
  isNullOrUndefined,
  scrollPage,
  useGetFarms,
  useGetFootprintingFarmFootprint,
  useNavigate,
  useTranslation,
} from "utils";

import { AggregatedFarmFootprint, FARM_FOOTPRINTING_HAS_LAND_CHANGED } from "models";

import { Card, ListCard } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { ELECTRICITY_BUCKETS, FarmElectricityForm, isElectricityBucketCovered } from "pages/app/footprinting/components/formFlows/farmFlow/FarmElectricityForm";
import { FarmFuelForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmFuelForm";
import { FarmLandUseForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmLandUseForm";
import { FarmCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintingFarmReviewProps {
  year?: number;
}

const HAS_LAND_CHANGED_TO_LABEL = {
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO]: "landUseForm.radioButton.no",
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES]: "landUseForm.radioButton.yes",
  [FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN]: "landUseForm.radioButton.dontKnow",
};

export const displayRadioAnswerSlug = (isActive: boolean | null) => (isActive === null ? "" : `footprinting:radioAnswer.${isActive}`);

export const electricityDataPairs = (
  t: TFunction,
  capabilities: FarmFootprintingCapabilities,
  footprint?: Pick<AggregatedFarmFootprint, (typeof ELECTRICITY_BUCKETS)[number]["field"]>
) =>
  ELECTRICITY_BUCKETS.filter((bucket) => isElectricityBucketCovered(bucket, capabilities)).map(({ field, copyKey }) => ({
    label: t(`footprintingFarm:electricityForm.question.${copyKey}`),
    value: isNullOrUndefined(footprint?.[field]) ? "" : `${footprint?.[field]} kWh`,
  }));

export const arableLandDataPairs = (
  t: TFunction,
  footprint?: Pick<AggregatedFarmFootprint, "arableLandActive" | "overallSoilType" | "averageSoilSaturation">
) => [
  { label: t("footprintingFarm:landUseForm.arableLand.title"), value: t(displayRadioAnswerSlug(footprint?.arableLandActive ?? null)) },
  ...(footprint?.arableLandActive
    ? [
        {
          label: t("footprintingFarm:soilForm.soilTypeLabel"),
          value: footprint.overallSoilType ? t(`footprintingFarm:soilForm.soilTypeOptions.${footprint.overallSoilType}`) : "",
        },
        {
          label: t("footprintingFarm:soilForm.soilSaturationLabel"),
          value: footprint.averageSoilSaturation ? t(`footprintingFarm:soilForm.soilSaturationOptions.${footprint.averageSoilSaturation}`) : "",
        },
      ]
    : []),
];

export const FootprintingFarmReview = ({ year }: FootprintingFarmReviewProps) => {
  const { t } = useTranslation("footprintingFarm");
  const navigate = useNavigate();
  const { currentFarm, isLoading: isGetFarmsLoading } = useGetFarms();

  const farmFootprintQuery = useGetFootprintingFarmFootprint(currentFarm?.id, year);

  useEffect(() => {
    scrollPage();
  }, []);

  const naviagteToFormPage = (pageKey: string) => {
    navigate("/footprinting/form", { state: { year, formType: "farm", pageKey } });
  };

  const loading = isGetFarmsLoading || farmFootprintQuery.isLoading;

  return (
    <>
      <Card className="padding-125">
        <ReviewPageTitle title={t("footprinting:review.farm.title")} iconName="klim/farm" isLoading={loading} />
        {/* Farm electricity form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.farm.electricity")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(FarmElectricityForm.Key) }}
          dataPairs={[
            ...electricityDataPairs(t, getFarmFootprintingCapabilities(currentFarm?.carbonProgram ?? undefined), farmFootprintQuery.data?.footprint),
            { label: t("electricityForm.question.renewables"), value: `${formatFractionInput(farmFootprintQuery.data?.footprint?.renewablesPct ?? 0)} %` },
          ]}
        />
        {/* Farm fuel form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.farm.fuel")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(FarmFuelForm.Key) }}
          dataPairs={[
            { label: t("fuelForm.question.dieselQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.dieselL} L` },
            { label: t("fuelForm.question.petrolQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.petrolL} L` },
            { label: t("fuelForm.question.naturalGasQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.naturalGasM3} m³` },
            { label: t("fuelForm.question.heatingOilQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.heatingOilL} L` },
          ]}
        />
        {/* Land Use Change form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.farm.landuse")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(FarmLandUseForm.Key) }}
          dataPairs={[
            ...arableLandDataPairs(t, farmFootprintQuery.data?.footprint),
            {
              label: t("landUseForm.question.general"),
              value: t(HAS_LAND_CHANGED_TO_LABEL[farmFootprintQuery.data?.footprint?.hasLandChanged ?? FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN]),
              children:
                (farmFootprintQuery.data?.footprint?.landUseChangeUsage.length ?? 0) > 0
                  ? farmFootprintQuery.data?.footprint?.landUseChangeUsage
                      .filter((luc) => luc.hasLandChanged === "yesLUC" && (luc.areaConvertedHa ?? 0) > 0)
                      .map((luc, index) => (
                        <ListCard
                          key={luc.id}
                          isSublist
                          isLoading={loading}
                          title={t("landUseForm.question.title") + (index + 1)}
                          dataPairs={[
                            {
                              label: t("landUseForm.question.previousLandChange"),
                              value: luc.previousLandUseType ?? "",
                            },
                            {
                              label: t("landUseForm.question.conversionYear"),
                              value: `${luc.yearOfConversion}`,
                            },
                            {
                              label: t("landUseForm.question.areaConverted"),
                              value: `${luc.areaConvertedHa} Ha`,
                            },
                          ]}
                        />
                      ))
                  : null,
            },
          ]}
        />
        {/* Comment */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:commentForm.title")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(FarmCommentForm.Key) }}
          dataPairs={[
            {
              label: t("footprinting:commentForm.label"),
              value: farmFootprintQuery.data?.footprint?.comment || t("footprinting:commentForm.noComment"),
            },
          ]}
        />
      </Card>
    </>
  );
};
